import { Grid } from '@mui/material';
import dayjs from 'dayjs';
import { PotentialMatchesTable } from '../../components/tables';
import { t } from 'i18next';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { FilterKey, PotentialOffer, TruckDetails } from '../../types';
import { useVehicleContext } from '../../context/vehicleContext';
import { useNegotiationAlerts } from '../../context/negotiationAlertsContext';
import { getCountryDisplay } from '../../utils/countryUtils';
import { useAnalytics, useAutoRefresh, useEngagementTracking, useLocalStorage, useTenant } from '../../hooks';
import { PotentialOffersOrderBy } from '../loadSearch/loadSearch.page';
import { Considerations, defaultConsiderations } from '../../models/considerations';
import { VehicleAutoSearch } from '../../models/vehicleAutoSearch';
import { fetchVehicleNegotiatedOffers } from '../../services/vehicleService';
import FleetTable from '../../components/tables/fleetTable/fleetTable';
import useCollapse from '../../hooks/ui/useCollapse';
import { VehicleAutoNegoSession, AutoNegoState } from '../../models/vehicleAutoNegoSession';
import { getAutoSessions } from '../../services/autoSessionService';
import { toast } from '../../utils/toast';
import { PotentialOffersFilter } from '../../models/potentialOffersFilter';

export const Negotiations: React.FC = () => {
  const { vehicles, fetchVehicles } = useVehicleContext();
  const { refresh: refreshNegotiationAlerts } = useNegotiationAlerts();

  // Analytics tracking
  const { trackFeatureUsage } = useAnalytics();
  useEngagementTracking('load_search');

  // Truck selection (ID only - TruckDetails derived from vehicles)
  const [selectedTruckId, setSelectedTruckId] = useState<number | null>(() => {
    const savedTruckId = localStorage.getItem('selectedTruckId');
    return savedTruckId ? Number(savedTruckId) : null;
  });

  // Tenant data
  const { tenant: tenantData } = useTenant();
  const [pageSize] = useState<number>(15);

  // Ongoing offers state
  const [offers, setOffers] = useState<PotentialOffer[]>([]);
  const [offerLoading, setOfferLoading] = useState<boolean>(false);
  const [totalOffers, setTotalOffers] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const [orderBy, setOrderBy] = useLocalStorage<PotentialOffersOrderBy>('negotiationsOrderBy', undefined);
  const [order, setOrder] = useLocalStorage<'ASC' | 'DESC' | undefined>('negotiationsOrder', undefined);
  const [stats, setStats] = useState<{ unreadEmails: number, needHuman: number, waitingForClient: number } | undefined>(undefined);

  // Filter state (for potential offers table)
  const [considerations, setConsiderations] = useLocalStorage<Considerations>('negotiationsConsiderations', defaultConsiderations);
  // Default must be applied in the lazy initializer (not in useEffect): ConsiderationsFilter's
  // checkbox-sync effect wipes startTime if its checkbox initialized to off, racing any later set.
  const FILTERS_KEY = 'notea-negotiationsFilters';
  const FILTERS_SENTINEL_KEY = 'notea-negotiationsLoadingDefaultV1';
  const [filters, setFiltersState] = useState<PotentialOffersFilter>(() => {
    let stored: PotentialOffersFilter = {};
    try {
      const raw = localStorage.getItem(FILTERS_KEY);
      stored = raw ? JSON.parse(raw) : {};
    } catch { /* */ }
    if (!localStorage.getItem(FILTERS_SENTINEL_KEY)) {
      if (!stored.startTime) {
        stored = { ...stored, startTime: dayjs().startOf('day').subtract(14, 'day').toDate() };
      }
      localStorage.setItem(FILTERS_SENTINEL_KEY, '1');
      localStorage.setItem(FILTERS_KEY, JSON.stringify(stored));
    }
    return stored;
  });
  const setFilters = useCallback((next: PotentialOffersFilter | ((prev: PotentialOffersFilter) => PotentialOffersFilter)) => {
    setFiltersState((prev) => {
      const value = typeof next === 'function' ? (next as (p: PotentialOffersFilter) => PotentialOffersFilter)(prev) : next;
      try {
        localStorage.setItem(FILTERS_KEY, JSON.stringify(value));
      } catch { /* */ }
      return value;
    });
  }, []);

  // Auto sessions state
  const [autoSearchSessions, setAutoSearchSessions] = useState<Map<number, VehicleAutoSearch | null>>(new Map());
  const [autoNegoSessions, setAutoNegoSessions] = useState<Map<number, VehicleAutoNegoSession | null>>(new Map());
  const [autoSessionsLoading, setAutoSessionsLoading] = useState<Set<number>>(new Set());

  const { countDown, manualRefresh, refreshTrigger } = useAutoRefresh({
    countdownSeconds: 60,
    enabled: !!selectedTruckId,
  });

  /** Same as Load Search ongoing table: optimistic close + filter stale refetches */
  const recentlyClosedOfferIdsRef = useRef<Set<number>>(new Set());

  const handleNegotiationClosed = useCallback((offer: PotentialOffer) => {
    flushSync(() => {
      recentlyClosedOfferIdsRef.current.add(offer.id);
      setOffers((prev) => prev.filter((o) => o.id !== offer.id));
      setTotalOffers((prev) => Math.max(0, prev - 1));
    });
  }, []);

  const handleNegotiationCloseFailed = useCallback((offer: PotentialOffer) => {
    recentlyClosedOfferIdsRef.current.delete(offer.id);
    manualRefresh();
  }, [manualRefresh]);

  const { CollapseButton, isCollapsed } = useCollapse('fleetTableCollapse', false);

  useEffect(() => {
    fetchVehicles({ with_drivers: true });
  }, [fetchVehicles]);

  const fetchAutoSessions = useCallback(async (): Promise<void> => {
    const vehicleIds = vehicles.map(v => v.id);
    if (vehicleIds.length === 0) return;

    setAutoSessionsLoading(new Set(vehicleIds));

    try {
      const { autoSearches, autoNegoSessions: negoSessions } = await getAutoSessions(vehicleIds);
      setAutoSearchSessions(new Map(Object.entries(autoSearches).map(([k, v]) => [Number(k), v])));
      setAutoNegoSessions(new Map(Object.entries(negoSessions).map(([k, v]) => [Number(k), v])));
    } catch {
      console.error('Failed to fetch auto sessions');
    } finally {
      setAutoSessionsLoading(new Set());
    }
  }, [vehicles]);

  useEffect(() => {
    if (vehicles.length > 0) {
      fetchAutoSessions();
    }
  }, [vehicles, fetchAutoSessions]);

  const negotiationVehicles = useMemo(() => vehicles.filter((vehicle) => {
    const session = autoNegoSessions.get(vehicle.id);
    if (!session) return false;

    const hasActiveNegotiations = (session.currentActiveNegos ?? 0) > 0;
    const isSessionRunning = [
      AutoNegoState.ON,
      AutoNegoState.WAITING_RESULTS,
      AutoNegoState.RESULT_TO_REVIEW,
    ].includes(session.autoNegoState);

    return hasActiveNegotiations || isSessionRunning;
  }), [vehicles, autoNegoSessions]);

  const handleAutoSessionSuccess = useCallback((): void => {
    fetchAutoSessions();
    toast.success(t('Session updated successfully'));
  }, [fetchAutoSessions, t]);

  // Keep selected truck within the negotiations-only fleet subset.
  useEffect(() => {
    if (negotiationVehicles.length === 0) {
      if (selectedTruckId !== null) {
        setSelectedTruckId(null);
        localStorage.removeItem('selectedTruckId');
      }
      return;
    }

    const selectedExists = selectedTruckId !== null && negotiationVehicles.some((v) => v.id === selectedTruckId);
    if (!selectedExists) {
      const fallbackTruckId = negotiationVehicles[0].id;
      setSelectedTruckId(fallbackTruckId);
      localStorage.setItem('selectedTruckId', fallbackTruckId.toString());
      setPage(1);
    }
  }, [negotiationVehicles, selectedTruckId]);

  // Derived truck details
  const selectedTruck = useMemo((): TruckDetails | null => {
    if (!selectedTruckId) return null;

    const vehicle = negotiationVehicles.find((v) => v.id === selectedTruckId);
    if (!vehicle) return null;

    return {
      id: selectedTruckId.toString(),
      plate: vehicle.truckPlate || 'N/A',
      truckPlate: vehicle.truckPlate,
      trailerPlate: vehicle.trailerPlate,
      driver: vehicle.name || 'Unknown',
      location: vehicle.currentPosition
        ? `${getCountryDisplay(vehicle.currentPosition.country)}, ${vehicle.currentPosition.postalCode}`
        : 'Unknown',
      equipment: Array.isArray(vehicle.equipment) ? vehicle.equipment.join(', ') : vehicle.equipment || 'Standard',
      status: vehicle.isActive ? 'active' : 'inactive',
      matches: [],
    };
  }, [selectedTruckId, negotiationVehicles, offers, totalOffers]);

  const handleOngoingPageChange = useCallback((newPage: number) => {
    setPage(newPage);
    trackFeatureUsage('load_search', 'page_changed', { page: newPage, table: 'ongoing_offers' });
  }, [trackFeatureUsage]);

  const handleOngoingOfferSortChange = useCallback((newOrderBy: PotentialOffersOrderBy) => {
    trackFeatureUsage('load_search', 'sort_changed', { column: newOrderBy || 'none', table: 'ongoing_offers' });
    // If clicking the same column, cycle through: DESC → ASC → Neutral
    if (orderBy === newOrderBy) {
      if (order === 'DESC') {
        setOrder('ASC');
      } else if (order === 'ASC') {
        setOrder(undefined);
        setOrderBy(undefined);
      }
    } else {
      // If clicking a different column, start with DESC
      setOrderBy(newOrderBy);
      setOrder('DESC');
    }
  }, [orderBy, order, setOrder, setOrderBy, trackFeatureUsage]);


  const handleTruckClick = useCallback((vehicleId: number) => {
    if (vehicleId === selectedTruckId) return;

    trackFeatureUsage('load_search', 'truck_selected', { vehicle_id: vehicleId });

    setPage(1);

    setSelectedTruckId(vehicleId);
    localStorage.setItem('selectedTruckId', vehicleId.toString());
  }, [selectedTruckId, trackFeatureUsage]);


  // Fetch ongoing offers when selection or filters change
  // Note: Ongoing offers always use 'default' filter mode (independent of main table)
  useEffect(() => {
    if (!selectedTruckId) return;

    const loadOngoingOffers = async () => {
      setOfferLoading(true);
      try {
        const limit = pageSize;
        const offset = (page - 1) * limit;
        const { offers: fetchedOffers, count, negotiationsStats } = await fetchVehicleNegotiatedOffers(
          selectedTruckId,
          considerations,
          offset,
          limit,
          orderBy,
          order,
          filters
        );

        const staleClosed = recentlyClosedOfferIdsRef.current;
        [...staleClosed].forEach((id) => {
          if (!fetchedOffers.some((o) => o.id === id)) {
            staleClosed.delete(id);
          }
        });

        const filteredOffers = fetchedOffers.filter((o) => !staleClosed.has(o.id));
        const hiddenOnPage = fetchedOffers.length - filteredOffers.length;

        setOffers(filteredOffers);
        setTotalOffers(Math.max(0, count - hiddenOnPage));
        setStats(negotiationsStats);
        refreshNegotiationAlerts();
      } catch {
        // Error already shown in service
        // showErrorToast(undefined, t('Failed to fetch offers for the selected truck!'));
        setOffers([]);
      } finally {
        setOfferLoading(false);
      }
    };

    loadOngoingOffers();
  }, [selectedTruckId, page, order, orderBy, considerations, filters, refreshTrigger, t]);


  return (
    <>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: isCollapsed ? 2 : 3 }} sx={{ height: '100%' }} className="grid-animated">
          <FleetTable
            vehicles={negotiationVehicles}
            selectedTruck={selectedTruck}
            autoSearchSessions={autoSearchSessions}
            autoNegoSessions={autoNegoSessions}
            loadingSessions={autoSessionsLoading}
            isCollapsed={isCollapsed}
            CollapseButton={CollapseButton}
            onTruckClick={handleTruckClick}
            onAutoSessionSuccess={handleAutoSessionSuccess}
            showAIColumn={false}
          />
        </Grid>
        <Grid size={{ xs: 12, md: isCollapsed ? 10 : 9 }} className="grid-animated">
          <PotentialMatchesTable
            mode="ongoing"
            confirmStaleOnOpen
            title={t('Negotiations')}
            selectedTruck={selectedTruck}
            offers={offers}
            tenantData={tenantData}
            totalOffers={totalOffers}
            stats={stats}
            vehicleOffersLoading={offerLoading}
            page={page}
            pageSize={pageSize}
            orderBy={orderBy}
            order={order}
            considerations={considerations}
            filterMode={'default'}
            hiddenFilters={new Set<FilterKey>(['autoSearchSync'])}
            autoSearchSession={null}
            useAutoSearchSync={false}
            filters={filters}
            refreshCountdown={countDown}
            containerHeight={'calc(100vh - 2rem)'}
            tableContainerMaxHeight={'calc(100vh - 14rem)'}
            onPageChange={handleOngoingPageChange}
            onSortChange={handleOngoingOfferSortChange}
            onFiltersChange={setFilters}
            onConsiderationsChange={setConsiderations}
            onRefresh={manualRefresh}
            onNegotiationClosed={handleNegotiationClosed}
            onNegotiationCloseFailed={handleNegotiationCloseFailed}
          />
        </Grid>
      </Grid>
    </>
  );
};